// Every Firestore access for the auto-dispatch flow lives here.
// The page must never touch `db` directly -- the same contract bookingService.js
// keeps for the จองวันส่งสินค้า flow.
//
// This module is deliberately thin. All the judgement -- who goes where, in what
// order, at what cost -- is in dispatchPlan.js, which is pure and covered by
// `npm run test:dispatch`. What lives here is reading the day, writing the
// result, and the safety rules around that write.
//
// ---------------------------------------------------------------------------
// Why the write is the dangerous part
// ---------------------------------------------------------------------------
//
// commitPlan writes into `booking`, the collection carBooking.vue renders and
// the delivery team edits by hand. Firestore rules are wide open (see the
// Security section of CLAUDE.md), so nothing below the app enforces any of
// this. Three rules keep an auto-plan from destroying manual work:
//
//   1. NOTHING WITHOUT `autoAssigned` IS EVER TOUCHED. An appointment the team
//      made or moved by hand has no such field, so a re-plan cannot see it,
//      let alone delete it.
//   2. Re-planning REPLACES by `sourceSlotId` rather than appending, so
//      pressing the button twice cannot double-book a customer.
//   3. Deletes happen in the same batch as the writes that supersede them, so
//      a day is never briefly empty.
//
// `preview` is the default posture: planDay() only reads. Writing takes a
// separate, explicit call.

const { db } = require('@/firebase')
const R = require('./bookingRules')
const dispatchPlan = require('./dispatchPlan')
const plannerClient = require('./plannerClient')
const moment = require('moment-timezone')
const driverDay = require('./driverDay')
const RJ = require('./returnJob')
const DR = require('./deliveryRow')
const bookingService = require('./bookingService')
const zoneDv = require('../zonedv.json')

const BOOKING_COL = () => db.collection('booking')
const SLOT_COL = () => db.collection('bookingSlot')
const CAR_COL = () => db.collection('registerCar')
const TRIP_COL = () => db.collection('outOfAreaTrip')

// Firestore caps a batch at 500 operations. bookingService.js uses the same
// margin for the same reason: a delete and a write can both land in one pass.
const BATCH_LIMIT = 450

async function commitChunked (ops) {
  for (let i = 0; i < ops.length; i += BATCH_LIMIT) {
    const batch = db.batch()
    ops.slice(i, i + BATCH_LIMIT).forEach(op => {
      if (op.kind === 'delete') batch.delete(op.ref)
      else if (op.kind === 'update') batch.update(op.ref, op.data)
      else batch.set(op.ref, op.data, { merge: true })
    })
    await batch.commit()
  }
}

// bookingDay.carriedRoutes for a route's later days (R.routeHoldWrites): held
// while it runs 2 days, released otherwise. Set with merge, so the entry is
// written whole and nothing else on the day is touched.
// Nothing at all when the route neither runs nor ran more than one day, so a
// one-day route never creates a bookingDay document for the day after it.
function holdOps (tripId, trip, days) {
  if ((Number(days) || 1) <= 1 && (Number(trip.days) || 1) <= 1) return []
  return R.routeHoldWrites(tripId, trip, days).map(w => ({
    kind: 'set',
    ref: db.collection('bookingDay').doc(w.date),
    data: { date: w.date, carriedRoutes: { [tripId]: w.entry } }
  }))
}

// The later day a hold just landed on, when it is now booked past what it can
// take -- a warning for the page, never a refusal: the car is really away.
// -> { date, total, cap } | null. A failed read says nothing rather than fail
// a send that has already been written.
async function overHeldDay (tripId, trip, days) {
  const held = R.routeHoldWrites(tripId, trip, days).filter(w => w.entry.held)
  if (!held.length) return null
  try {
    const [cfg, snap] = await Promise.all([
      bookingService.getConfig(),
      db.collection('bookingDay').doc(held[0].date).get()
    ])
    const day = snap.exists ? snap.data() : null
    if (!R.overAfterHold(day, cfg.cars, cfg.perCar)) return null
    return { date: held[0].date, total: Number(day.total) || 0, cap: R.capacityForDay(day, cfg.cars, cfg.perCar).total }
  } catch (e) {
    return null
  }
}

// Zone lookup for the documents carBooking.vue renders. Built once from
// zonedv.json and keyed by the same "ตำบล,อำเภอ,จังหวัด" string
// bookingRules.formatLocation() writes, so a bookingSlot's `location` is a
// direct key.
let ZONES = null
function zoneIndex () {
  if (ZONES) return ZONES
  ZONES = {}
  zoneDv.forEach(row => {
    const key = R.formatLocation({ subDt: row.subDt, district: row.district, province: row.province })
    if (!ZONES[key]) ZONES[key] = { zoneMain: row.zoneMain, zoneDv: row.zoneDv || [] }
  })
  return ZONES
}

function zoneOf (location) {
  return zoneIndex()[location] || { zoneMain: null, zoneDv: [] }
}

// The small fleet, with the cost and range attributes the planner needs.
// fleet.js reads the same collection but returns only plate strings, because
// that is all the three views calling it want; this needs the whole document.
async function loadVehicles () {
  const snapshot = await CAR_COL().get()
  const vehicles = []
  snapshot.forEach(doc => {
    const d = doc.data()
    // Skips the legacy carSmall / carLarge documents, which carry no `size`.
    if (!d || !d.size) return
    vehicles.push({
      regNum: d.regNum || doc.id,
      size: d.size,
      order: d.order,
      fuelType: d.fuelType,
      label: d.label,
      costPerKm: d.costPerKm,
      rangeKm: d.rangeKm,
      spare: !!d.spare
    })
  })
  if (!vehicles.length) throw new Error('registerCar has no per-plate vehicle documents')
  return vehicles
}

// The day's work: every active booking on that date that this planner can
// actually route.
//
// Cancelled bookings are dropped here rather than in the planner, because
// `isActive` is bookingRules' definition and the planner should not need to
// know that 'pending' and 'confirmed' are pre-1.6.0 statuses that still count.
//
// Out-of-area bookings are dropped for a different reason, and it is not
// squeamishness: every model behind the plan is built for the routine service
// area. geo.js resolves a point and travelTime.js chains legs from a depot in
// ไทรน้อย; a stop in ราชบุรี is not a leg those were calibrated on, and the
// range check that decides which car can take a round would be reading a
// distance no small car may be able to make. Mixed into an ordinary round it
// would not merely be planned badly -- it would move every stop after it.
//
// They are RETURNED rather than silently discarded (see `setAside`), because a
// day's work that vanished from the plan with no mention is how a customer
// gets forgotten -- and they are planned on their own, one route to one truck,
// by dispatchPlan.planTrips. Delivery still sends each route to จัดรถ by hand.
async function loadDayBookings (date) {
  const snap = await SLOT_COL().where('date', '==', date).get()
  const out = []
  const setAside = []
  snap.forEach(doc => {
    const b = doc.data()
    if (!R.isActive(b.status)) return
    if (b.fleet === 'large') return // up-country: a truck trip, not a small-car round
    const booking = Object.assign({ id: doc.id }, b)
    if (b.outOfArea) setAside.push(booking)
    else out.push(booking)
  })
  out.sort(R.compareForDispatch)
  setAside.sort(R.compareForDispatch)
  return { bookings: out, setAside: setAside }
}

// TRANSITION PERIOD -- see dispatchPlan.dispatchedConflicts. The bookings of
// `bookings` whose SO (a pickup: RT) is already on คิวรถเล็ก that day: in deliveryList, or in a
// booking job typed in by hand. Two reads by date alone.
async function loadDispatchedConflicts (date, bookings) {
  const [listSnap, jobSnap] = await Promise.all([
    db.collection('deliveryList').where('deliveryDate', '==', date).get(),
    BOOKING_COL().where('date', '==', date).get()
  ])
  const listRows = []
  listSnap.forEach(d => listRows.push(d.data()))
  const jobs = []
  jobSnap.forEach(d => jobs.push(d.data()))
  return dispatchPlan.dispatchedConflicts(bookings, listRows, jobs)
}

// Thrown by a writer that finds a conflict at the moment of writing -- an SO
// put on คิวรถเล็ก by hand after วางแผน read the day.
function alreadyDispatched (conflicts) {
  const err = new Error('ALREADY_DISPATCHED')
  err.conflicts = conflicts
  return err
}

// Read-only. Produces a plan and touches nothing.
//
// This is what the preview screen calls. Staff see the whole day before any of
// it is written, which is the difference between a tool they trust and one that
// rearranges the schedule the moment they open it.
async function planDay (date, options) {
  const opts = options || {}
  const [day, vehicles, carried] = await Promise.all([
    opts.bookings
      ? Promise.resolve({ bookings: opts.bookings, setAside: [] })
      : loadDayBookings(date),
    opts.cars ? Promise.resolve(opts.cars) : loadVehicles(),
    loadCarriedPlates(date)
  ])

  // Both plans the page compares (see dispatchPlan.planOptions). The fewest-
  // charges plan is the one returned; the cheapest-fuel one rides on it as
  // `alternative`, null when it would not be a real choice. Planned in a Web
  // Worker -- see plannerClient.js for why.
  const choices = await plannerClient.planOptions({
    bookings: day.bookings,
    setAside: day.setAside,
    cars: vehicles,
    carried: carried,
    config: opts.config
  }, opts.onProgress)

  // `setAside` rides alongside the plan so the preview screen can say what it
  // is not showing. An out-of-area day with nothing else on it produces an
  // empty plan, and "no work today" would be a lie.
  // TRANSITION PERIOD: what is already on คิวรถเล็ก. The page refuses
  // ยืนยันแผนนี้ and ส่งเข้าคิวจัดรถ while any of it stands.
  const dispatched = opts.bookings
    ? []
    // A route this page sent reached คิวรถเล็ก the intended way, and once
    // confirmed there it is in deliveryList on purpose -- not a conflict.
    : await loadDispatchedConflicts(date, day.bookings.concat(day.setAside.filter(b => !b.sentToDispatchAt)))
  const common = { date: date, bookings: day.bookings, setAside: day.setAside, cars: vehicles, dispatched: dispatched }
  return Object.assign({}, common, choices.minCharge, {
    alternative: choices.minFuel ? Object.assign({}, common, choices.minFuel) : null
  })
}

// Everything this planner previously wrote for a date. The `autoAssigned`
// filter is the safety boundary: a manual appointment is invisible here, so
// nothing downstream can delete one.
async function loadAutoAssigned (date) {
  const snap = await BOOKING_COL()
    .where('date', '==', date)
    .where('autoAssigned', '==', true)
    .get()
  const out = []
  snap.forEach(doc => out.push(Object.assign({ id: doc.id }, doc.data())))
  return out
}

// Writes a plan into `booking`, replacing whatever this planner wrote for that
// date before.
//
// `dryRun` returns exactly what would change without writing, which is what the
// preview screen shows on the confirm dialog. Nothing here is destructive
// without an explicit call with dryRun false.
async function commitPlan (date, plan, options) {
  const opts = options || {}
  // Not Date.now(): a caller may want the version to match a label staff saw.
  const planVersion = opts.planVersion || new Date().toISOString()

  const existing = await loadAutoAssigned(date)
  const docs = dispatchPlan.toBookingDocs(plan, {
    date: date,
    user: opts.user || '',
    planVersion: planVersion,
    zoneOf: zoneOf
  })

  // Replace by sourceSlotId, so a booking that is still in the plan keeps its
  // document id and its history rather than being deleted and recreated.
  const bySource = {}
  existing.forEach(e => { if (e.sourceSlotId) bySource[e.sourceSlotId] = e })

  const ops = []
  const summary = { created: 0, updated: 0, removed: 0, slotsPatched: 0 }

  docs.forEach(entry => {
    const prior = entry.sourceSlotId ? bySource[entry.sourceSlotId] : null
    if (prior) {
      ops.push({ kind: 'set', ref: BOOKING_COL().doc(prior.id), data: entry.doc })
      delete bySource[entry.sourceSlotId]
      summary.updated++
    } else {
      ops.push({ kind: 'set', ref: BOOKING_COL().doc(), data: entry.doc })
      summary.created++
    }
  })

  // Whatever this planner wrote before and the new plan no longer contains --
  // a booking cancelled since, or one this run could not place. Deleted in the
  // same batch as the writes above so the day is never briefly empty.
  Object.keys(bySource).forEach(sourceId => {
    ops.push({ kind: 'delete', ref: BOOKING_COL().doc(bySource[sourceId].id) })
    summary.removed++
  })
  // Older rows this planner wrote before sourceSlotId existed, or rows whose
  // source booking is gone entirely, would otherwise linger forever.
  existing.filter(e => !e.sourceSlotId).forEach(e => {
    ops.push({ kind: 'delete', ref: BOOKING_COL().doc(e.id) })
    summary.removed++
  })

  // TRANSITION PERIOD: checked again here, not only at วางแผน -- an SO put on
  // คิวรถเล็ก by hand while the page was open would otherwise go in twice.
  const conflicts = await loadDispatchedConflicts(date,
    docs.map(e => ({ id: e.sourceSlotId, soNo: e.doc.soNo, rtNo: e.doc.rtNo, name: e.doc.name })))
  if (conflicts.length) throw alreadyDispatched(conflicts)

  const patches = dispatchPlan.toSlotPatches(plan, { planVersion: planVersion })
  patches.forEach(p => {
    ops.push({ kind: 'update', ref: SLOT_COL().doc(p.id), data: p.patch })
    summary.slotsPatched++
  })

  if (opts.dryRun) return Object.assign({ planVersion, dryRun: true, operations: ops.length }, summary)

  await commitChunked(ops)
  return Object.assign({ planVersion, dryRun: false, operations: ops.length }, summary)
}

// Removes this planner's assignments for a date and clears the fields it wrote
// back onto the bookings. Manual appointments are untouched, as everywhere else
// in this module.
async function clearPlan (date, options) {
  const opts = options || {}
  const existing = await loadAutoAssigned(date)

  const ops = existing.map(e => ({ kind: 'delete', ref: BOOKING_COL().doc(e.id) }))
  existing.forEach(e => {
    if (!e.sourceSlotId) return
    ops.push({
      kind: 'update',
      ref: SLOT_COL().doc(e.sourceSlotId),
      data: { regNum: null, stopOrder: null, etaStart: null, planVersion: null }
    })
  })

  if (opts.dryRun) return { dryRun: true, removed: existing.length, operations: ops.length }
  await commitChunked(ops)
  return { dryRun: false, removed: existing.length, operations: ops.length }
}

// How many appointments on a date were made by hand rather than by the planner.
//
// The safety boundary expressed as a number: this is exactly the set commitPlan
// will not touch. The confirm dialog shows it, because "manual work is safe" is
// a claim worth backing with a count rather than a reassurance.
async function countManualBookings (date) {
  const snap = await BOOKING_COL().where('date', '==', date).get()
  let n = 0
  snap.forEach(d => { if (d.data().autoAssigned !== true) n++ })
  return n
}

// Live view of one day's assignments, for a screen that should follow a manual
// change the delivery team makes in carBooking while it is open.
function subscribeAssignments (date, cb, onError) {
  return BOOKING_COL()
    .where('date', '==', date)
    .onSnapshot(snap => {
      const rows = []
      snap.forEach(d => rows.push(Object.assign({ id: d.id }, d.data())))
      cb(rows)
    }, onError)
}

// An out-of-area route, handed to จัดรถ.
//
// This closes the hole the trips feature opened. loadDayBookings sets these
// bookings aside -- rightly, since every model behind the planner is calibrated
// on the routine area -- but "set aside" meant no `booking` document was written
// for them AT ALL, so the only way to dispatch one was to retype it into
// carBooking by hand. And a hand-retyped job has no `geo` and no `landmark`:
// SAP had no column for either then, the booking was the only place they existed, and
// the driver ends up with นำทาง pointing at a district centroid. CLAUDE.md warns
// about exactly this ("If you add a hop into deliveryList, carry geo and
// landmark too") and this hop did not.
//
// The plate is chosen HERE rather than when the route was opened: a route opened
// three weeks out has no business claiming a truck that is free on the day.
//
// Idempotent by sourceSlotId, so pressing it again after a late booking joins
// the route sends only the new one. It deliberately does NOT close the route --
// a route that keeps filling after it was sent is ordinary, and ปิดรับ is the
// button that means "stop".
//
// With a planned route (dispatchPlan.planTrips / tripForCar) timed for this
// plate, each new document also carries its stop order and ETAs; documents
// already sent are not rewritten.
async function sendTripToDispatch (tripId, { regNum, user, route, days } = {}) {
  const plate = String(regNum || '').trim()
  if (!plate) throw new Error('NO_CAR')

  const tripRef = TRIP_COL().doc(tripId)
  const tripSnap = await tripRef.get()
  if (!tripSnap.exists) throw new Error('NO_TRIP')
  const trip = tripSnap.data()
  if (trip.cancelled) throw new Error('TRIP_CANCELLED')

  // จำนวนวัน, chosen when sending: a route that sleeps on the road keeps its
  // jobs (and its car) on day 2. A booking sent late onto the route takes the
  // number the route already has.
  const tripDays = Number(days) || Number(trip.days) || 1
  const tripEnd = tripDays > 1 ? DR.addDays(trip.date, tripDays - 1) : null

  // The route's order and times, ONLY when they were worked out for this
  // plate (tripStopFields checks). No route -- bookingDate's board -- sends as
  // it always has. A two-day plan carries each stop's day; sent as one day it
  // carries nothing (tripStopFields).
  const routed = dispatchPlan.tripStopFields(route, plate, trip.date, tripDays, { sentBefore: !!trip.sentAt })

  // Range on `date` alone and the rest in memory -- the same automatic-index
  // rule every other query in this flow follows.
  const snap = await SLOT_COL().where('date', '==', trip.date).get()
  const rows = []
  snap.forEach(d => {
    const b = d.data()
    if (b.tripId !== tripId) return
    if (!R.isActive(b.status)) return
    rows.push(Object.assign({ id: d.id }, b))
  })
  if (!rows.length) throw new Error('TRIP_EMPTY')

  // What this route has already sent. Read by date rather than by a
  // sourceSlotId `in` query, which caps at ten.
  const sentSnap = await BOOKING_COL().where('date', '==', trip.date).get()
  const alreadySent = {}
  sentSnap.forEach(d => {
    const b = d.data()
    if (b.sourceSlotId) alreadySent[b.sourceSlotId] = d.id
  })

  // TRANSITION PERIOD: an SO of this route already on คิวรถเล็ก by hand. Only
  // the bookings about to be sent: one this route sent earlier may have been
  // confirmed there since, which is the intended way.
  const conflicts = await loadDispatchedConflicts(trip.date,
    rows.filter(b => !alreadySent[b.id] && !b.sentToDispatchAt))
  if (conflicts.length) throw alreadyDispatched(conflicts)

  const stamp = new Date().toISOString()
  const ops = []
  let created = 0
  let skipped = 0

  rows.forEach(b => {
    if (alreadySent[b.id]) { skipped++; return }
    ops.push({
      kind: 'set',
      ref: BOOKING_COL().doc(),
      // autoAssigned FALSE, and this is the trap worth naming: loadAutoAssigned
      // filters on autoAssigned == true, and commitPlan DELETES every such row
      // that the new plan does not contain. Marking these true would mean the
      // next re-plan of this date silently deleted jobs that have dates and
      // customers behind them.
      data: Object.assign(dispatchPlan.bookingDocFrom(b, {
        date: trip.date,
        carName: plate,
        user: user || '',
        zoneOf: zoneOf
      }), routed[b.id] || {}, {
        autoAssigned: false,
        outOfArea: true,
        tripId: tripId,
        sentToDispatchAt: stamp,
        sentToDispatchBy: user || ''
      }, tripEnd ? { tripEnd } : {})
    })
    // The same patch a routed booking gets, minus what only a plan knows. It is
    // what stops the row looking unassigned on the booking list.
    ops.push({
      kind: 'update',
      ref: SLOT_COL().doc(b.id),
      data: { regNum: plate, sentToDispatchAt: stamp, tripDays: tripDays }
    })
    created++
  })

  if (created) {
    ops.push({
      kind: 'update',
      ref: tripRef,
      data: {
        regNum: plate,
        days: tripDays,
        sentAt: stamp,
        sentBy: user || '',
        history: (trip.history || []).concat([{
          action: 'sendToDispatch', regNum: plate, days: tripDays, jobs: created, by: user || '', at: stamp,
          planned: !!Object.keys(routed).length
        }])
      }
    })
    // The car is away on day 2 too: take it out of that day's capacity.
    ops.push(...holdOps(tripId, Object.assign({}, trip, { regNum: plate }), tripDays))
    await commitChunked(ops)
  }

  const overDay = created ? await overHeldDay(tripId, Object.assign({}, trip, { regNum: plate }), tripDays) : null
  return { created: created, skipped: skipped, regNum: plate, overDay: overDay }
}

// ยกเลิกการส่ง: takes a route back from คิวรถเล็ก so it can be planned and sent
// again. Only jobs still waiting there are deleted; dispatchPlan.unsendPlan
// decides, and refuses the whole route (UNSEND_BLOCKED, with `blocked`) when a
// job has been confirmed into deliveryList -- that one is deleted on คิวรถเล็ก,
// where the driver-stamp warning lives. Nothing here touches SAP, deliveryList
// or a driver's day.
//
// Three reads by date alone, the automatic-index rule this file keeps. Not a
// transaction (it cannot query); a job confirmed in the seconds between the
// read and the write would be deleted as a waiting one -- the same window
// every delete on คิวรถเล็ก has.
async function unsendTrip (tripId, { user } = {}) {
  const tripRef = TRIP_COL().doc(tripId)
  const tripSnap = await tripRef.get()
  if (!tripSnap.exists) throw new Error('NO_TRIP')
  const trip = tripSnap.data()

  const [slotSnap, jobSnap, listSnap] = await Promise.all([
    SLOT_COL().where('date', '==', trip.date).get(),
    BOOKING_COL().where('date', '==', trip.date).get(),
    db.collection('deliveryList').where('deliveryDate', '==', trip.date).get()
  ])
  const rows = []
  slotSnap.forEach(d => { const b = d.data(); if (b.tripId === tripId) rows.push(Object.assign({ id: d.id }, b)) })
  const jobs = []
  jobSnap.forEach(d => jobs.push(Object.assign({ id: d.id }, d.data())))
  const listRows = []
  listSnap.forEach(d => listRows.push(d.data()))

  const decision = dispatchPlan.unsendPlan(rows, jobs, listRows)
  if (!decision.ok) {
    const err = new Error(decision.blocked.length ? 'UNSEND_BLOCKED' : 'NOT_SENT')
    err.blocked = decision.blocked
    throw err
  }

  const stamp = new Date().toISOString()
  const ops = decision.deleteIds.map(id => ({ kind: 'delete', ref: BOOKING_COL().doc(id) }))
  decision.clearSlotIds.forEach(id => ops.push({
    kind: 'update',
    ref: SLOT_COL().doc(id),
    data: { regNum: null, sentToDispatchAt: null }
  }))
  ops.push({
    kind: 'update',
    ref: tripRef,
    data: {
      regNum: null,
      sentAt: null,
      sentBy: null,
      history: (trip.history || []).concat([{
        action: 'unsend', jobs: decision.deleteIds.length, by: user || '', at: stamp
      }])
    }
  })
  // No car on the route any more, so none held on day 2 either.
  ops.push(...holdOps(tripId, trip, 1))
  await commitChunked(ops)
  return { deleted: decision.deleteIds.length, cleared: decision.clearSlotIds.length }
}

// A pin saved on the planning page reaches the `booking` documents a route
// already sent to คิวรถเล็ก, which carry their own copy and would otherwise
// keep the blank one (dispatchPlan.sentPinPatches decides which). Read by date
// alone -- the automatic-index rule this file keeps -- and stamped as
// คิวรถเล็ก's own พิกัด dialog stamps them. Returns how many were updated.
async function pinSentBookings (date, slotIds, fields, user) {
  const snap = await BOOKING_COL().where('date', '==', date).get()
  const docs = []
  snap.forEach(d => docs.push(Object.assign({ id: d.id }, d.data())))
  // คิวรถเล็ก prints this field as it stands, so it is written in that
  // dialog's own format rather than ISO.
  const stamp = moment().tz('Asia/Bangkok').format('YYYY-MM-DD HH:mm:ss')
  const ops = dispatchPlan.sentPinPatches(docs, slotIds, fields).map(p => ({
    kind: 'update',
    ref: BOOKING_COL().doc(p.id),
    data: Object.assign({}, p.data, { geoUpdatedBy: user || '', geoUpdatedAt: stamp })
  }))
  if (ops.length) await commitChunked(ops)
  return ops.length
}

// --- พิกัด on a job already on คิวรถเล็ก / จัดรถ -------------------------------
// The พิกัด dialog (listPinDialog.vue) is shared by คิวรถเล็ก and จัดรถ, so the
// writes behind it live here, once, rather than in each page.

// Every deliveryList row of one ใบจัดรถ, for จัดรถ's พิกัด column. Equality only:
// no composite index.
async function loadSheetRows (transNo) {
  if (!transNo) return []
  const snap = await db.collection('deliveryList').where('transNo', '==', String(transNo)).get()
  const rows = []
  snap.forEach(d => rows.push(Object.assign({ id: d.id }, d.data())))
  return rows
}

// Saves พิกัด / จุดสังเกต on one row and returns the SAP documents it now
// covers -- { soNos, rtNos } (RJ.pinNumbers: a pickup's pin goes to its RT) --
// for the SAP write-back the dialog does afterwards.
//
// A deliveryList row (status 'SO', or 'RT' for a return) writes EVERY row of the same visit, not only
// the one clicked: the driver app groups a customer's SOs on one truck and day
// into one stop (driverDay.stopKeyFor), so a pin on one SO belongs to all of
// them -- and a second SO still showing "no pin" would invite someone to type a
// different one. Equality-only query: no composite index.
//
// A `booking` row (not yet confirmed) also writes its bookingSlot: a planned
// appointment is rebuilt from it on every re-plan, which would erase a pin
// written only on the booking.
async function savePin (row, fields, user) {
  const stamp = Object.assign({}, fields, {
    geoUpdatedBy: user || '',
    geoUpdatedAt: moment().tz('Asia/Bangkok').format('YYYY-MM-DD HH:mm:ss')
  })
  if (!RJ.isListRow(row)) {
    await BOOKING_COL().doc(row.id).update(stamp)
    if (row.sourceSlotId) {
      await bookingService.setPin(row.sourceSlotId, { geo: fields.geo, landmark: fields.landmark, user })
    }
    return RJ.pinNumbers([row])
  }
  const key = driverDay.stopKeyFor(row)
  const snap = await db.collection('deliveryList')
    .where('deliveryDate', '==', row.deliveryDate)
    .where('regNum', '==', row.regNum)
    .get()
  const ids = new Set([row.id])
  const rows = [row]
  snap.forEach(doc => {
    if (driverDay.stopKeyFor(doc.data()) === key && !ids.has(doc.id)) {
      ids.add(doc.id)
      rows.push(doc.data())
    }
  })
  const batch = db.batch()
  ids.forEach(id => batch.update(db.collection('deliveryList').doc(id), stamp))
  await batch.commit()
  return RJ.pinNumbers(rows)
}

// แก้จำนวนวัน on a sent route: the route's `days`, the `booking` jobs still
// waiting on คิวรถเล็ก, and the route's rows already confirmed into
// deliveryList (found by date + plate, then by the route's SOs -- equality
// only). 1 -> 2 after day 1 is "could not finish, carry on tomorrow".
async function setTripDays (tripId, { days, user } = {}) {
  const n = Number(days)
  if (n !== 1 && n !== 2) throw new Error('BAD_DAYS')
  const tripRef = TRIP_COL().doc(tripId)
  const snap = await tripRef.get()
  if (!snap.exists) throw new Error('NO_TRIP')
  const trip = snap.data()
  if (!trip.sentAt || !trip.regNum) throw new Error('NOT_SENT')
  // Back to one day: a stop the plan put on day 2 is now on the departure day,
  // or it would wait under แผนวันถัดไป for a day the route no longer has --
  // remembered in planDateWas, so going back to 2 days restores it. Only where
  // a planDate exists -- an unplanned route gets none invented (DR.daysChange).
  const dayFields = x => DR.daysChange(x, trip.date, n)
  const [slots, jobs, list] = await Promise.all([
    SLOT_COL().where('date', '==', trip.date).get(),
    BOOKING_COL().where('date', '==', trip.date).get(),
    db.collection('deliveryList').where('deliveryDate', '==', trip.date).where('regNum', '==', trip.regNum).get()
  ])
  const sos = {}
  const ops = []
  slots.forEach(d => {
    const b = d.data()
    if (b.tripId !== tripId) return
    if (b.soNo) sos[String(b.soNo)] = true
    if (b.rtNo) sos['rt:' + b.rtNo] = true
    ops.push({ kind: 'update', ref: SLOT_COL().doc(d.id), data: { tripDays: n } })
  })
  let jobCount = 0
  jobs.forEach(d => { if (d.data().tripId === tripId) { jobCount++; ops.push({ kind: 'update', ref: BOOKING_COL().doc(d.id), data: dayFields(d.data()) }) } })
  let rowCount = 0
  list.forEach(d => {
    const r = d.data()
    if (!sos[String(r.soNo)] && !(r.rtNo && sos['rt:' + r.rtNo])) return
    rowCount++
    ops.push({ kind: 'update', ref: db.collection('deliveryList').doc(d.id), data: dayFields(r) })
  })
  ops.push({ kind: 'update', ref: tripRef, data: { days: n, history: (trip.history || []).concat([{ action: 'days', from: trip.days || 1, to: n, by: user || '', at: new Date().toISOString() }]) } })
  ops.push(...holdOps(tripId, trip, n))
  await commitChunked(ops)
  return { jobs: jobCount, rows: rowCount, overDay: await overHeldDay(tripId, trip, n) }
}

// The cars still out on day 2 of a route sent the day before. Date-only filter,
// like subscribeTrips: no index.
async function loadCarriedPlates (date) {
  const snap = await TRIP_COL().where('date', '==', DR.addDays(date, -1)).get()
  const routes = []
  snap.forEach(d => routes.push(Object.assign({ id: d.id }, d.data())))
  return dispatchPlan.carriedPlates(routes, date)
}

// คิวรถเล็ก's grid: 'date|plate' -> provinces, for each later day of a sent
// multi-day route in [from - 1, to]. A range on `date` alone: no index.
async function loadRouteHolds (from, to) {
  const snap = await TRIP_COL().where('date', '>=', DR.addDays(from, -1)).where('date', '<=', to).get()
  const routes = []
  snap.forEach(d => routes.push(Object.assign({ id: d.id }, d.data())))
  const out = {}
  dispatchPlan.routeDayHolds(routes).forEach(h => { out[h.date + '|' + h.regNum] = h.provinces })
  return out
}


module.exports = {
  zoneOf,
  setTripDays,
  loadCarriedPlates,
  loadRouteHolds,
  pinSentBookings,
  loadSheetRows,
  savePin,
  unsendTrip,
  loadVehicles,
  loadDayBookings,
  sendTripToDispatch,
  planDay,
  loadAutoAssigned,
  countManualBookings,
  commitPlan,
  clearPlan,
  subscribeAssignments
}
